import React, { useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, Input2FA } from '@node-space/ui-components'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import { Address, BeneficiaryTransferDestination } from 'types/beneficiaries'
import { getAddress } from 'utils/beneficiaries'
import { BankDetailsConfirmationCallout } from './BankDetailsConfirmationCallout'

type FiatInstrumentConfirmationProps = {
  formFieldLabels: FormFieldLabels
  formValues: ContactFiatInstrumentFormData
  isLoading: boolean
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
}

export const FiatInstrumentConfirmation = ({
  formFieldLabels,
  formValues,
  isLoading,
  setValue,
}: FiatInstrumentConfirmationProps) => {
  const { t } = useTranslation()
  const [isBankGbpBankDetailsConfirmed, setIsBankGbpBankDetailsConfirmed] = useState(false)

  const isGBPBeneficiary = formValues?.currency === 'GBP'
  const is2FaEnabled = (isGBPBeneficiary && isBankGbpBankDetailsConfirmed) || !isGBPBeneficiary
  const isSWIFT = formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL

  const bankAddressData: Address = {
    addressLine1: formValues?.bankAddressLine1,
    addressLine2: formValues?.bankAddressLine2,
    city: formValues?.bankCity,
    postCode: formValues?.bankPostCode,
    region: formValues?.bankRegion,
    country: formValues?.bankCountryCode,
  }

  const intermediaryBankAddressData: Address = {
    addressLine1: formValues?.intermediaryBankAddressLine1,
    addressLine2: formValues?.intermediaryBankAddressLine2,
    city: formValues?.intermediaryBankCity,
    postCode: formValues?.intermediaryBankPostCode,
    region: formValues?.intermediaryBankRegion,
    country: formValues?.intermediaryBankCountryCode,
  }

  const bankAddress = getAddress(bankAddressData)
  const intermediaryBankAddress = getAddress(intermediaryBankAddressData)

  const handleBankDetailsConfirmationChange = (isChecked: boolean) => {
    setIsBankGbpBankDetailsConfirmed(isChecked)
  }

  const handleOn2FAChange = (value: string) => {
    setValue('twoFactorCode', value)
  }

  return (
    <Box width="full">
      {/* Beneficiary details */}
      <Box>
        <DataListItem
          id="currency"
          title={t('formFields.currency')}
          body={formValues?.currency}
          layout={'horizontal'}
        />
        <DataListItem
          id="description"
          title={t('formFields.beneficiaryDescription')}
          body={formValues?.description}
          layout={'horizontal'}
        />
        {formValues?.paymentReference && (
          <DataListItem
            id="paymentReference"
            title={t('formFields.paymentReference')}
            body={formValues?.paymentReference}
            layout={'horizontal'}
          />
        )}
      </Box>

      {/* Bank Details */}
      <Box>
        <DataListItem
          id="accountNumber"
          title={formFieldLabels?.accountNumber}
          body={formValues?.accountNumber}
          layout={'horizontal'}
        />
        <DataListItem
          id="bankCode"
          title={formFieldLabels?.bankCode}
          body={formValues?.bankCode}
          layout={'horizontal'}
        />
        {formValues?.routingNumber && (
          <DataListItem
            id="routingNumber"
            title={t('formFields.routingNumber')}
            body={formValues?.routingNumber}
            layout={'horizontal'}
          />
        )}
        {formValues?.bankName && (
          <DataListItem
            id="bankCode"
            title={t('formFields.bankName')}
            body={formValues?.bankName}
            layout={'horizontal'}
          />
        )}
        {bankAddress && (
          <DataListItem
            id="bankAddress"
            title={t('address')}
            body={bankAddress}
            layout={'horizontal'}
          />
        )}
      </Box>
      {/* IntermediaryBankDetails */}
      {isSWIFT && (
        <Box paddingT={20}>
          <h2 className="text-foreground-prominent text-h2">
            {t('manageBeneficiaries.intermediaryBankDetails')}
          </h2>
          <DataListItem
            id="intermediaryBankName"
            title={t('manageBeneficiaries.intermediarysBankName')}
            body={formValues?.intermediaryBankName}
            layout={'horizontal'}
          />
          <DataListItem
            id="intermediaryBankName"
            title={t('manageBeneficiaries.bicSwiftCode')}
            body={formValues?.intermediaryBankCode}
            layout={'horizontal'}
          />
          {formValues?.intermediaryBankRoutingNumber && (
            <DataListItem
              id="routingNumber"
              title={t('formFields.routingNumber')}
              body={formValues?.intermediaryBankRoutingNumber}
              layout={'horizontal'}
            />
          )}
          {intermediaryBankAddress && (
            <DataListItem
              id="bankAddress"
              title={t('address')}
              body={intermediaryBankAddress}
              layout={'horizontal'}
            />
          )}
        </Box>
      )}

      {formValues?.currency === 'GBP' && (
        <Box paddingT={12}>
          <BankDetailsConfirmationCallout
            onDetailsConfirmationChange={handleBankDetailsConfirmationChange}
          />
        </Box>
      )}

      <Box width="full" paddingB={12}>
        <Input2FA
          testId="add-fiat-instrument-2fa"
          disabled={!is2FaEnabled}
          loading={isLoading}
          onChange={handleOn2FAChange}
          value={formValues?.twoFactorCode}
        />
      </Box>
    </Box>
  )
}
